// src/components/ParticipantThumbnail.tsx
import React, { useEffect, useRef } from "react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Mic, MicOff, Monitor, Video, VideoOff } from "lucide-react";

interface ParticipantThumbnailProps {
  name: string;
  avatarSrc?: string;
  isMuted: boolean;
  isVideoOff: boolean;
  isSelf?: boolean;
  mediaStream?: MediaStream | null;
  isScreenShareThumbnail?: boolean;
  isActiveSpeaker?: boolean;
}

const ParticipantThumbnail: React.FC<ParticipantThumbnailProps> = ({
  name,
  avatarSrc,
  isMuted,
  isVideoOff,
  isSelf = false,
  mediaStream,
  isActiveSpeaker = false,
  isScreenShareThumbnail = false,
}) => {
  const videoRef = useRef<HTMLVideoElement>(null);

  useEffect(() => {
    if (videoRef.current && mediaStream) {
      videoRef.current.srcObject = mediaStream;
      videoRef.current.play().catch((error) => {
        console.warn(
          `ParticipantThumbnail for ${name}: Video play failed:`,
          error,
        );
      });
    }
  }, [mediaStream]);
  useEffect(() => {
    if (!videoRef.current || !mediaStream) return;

    if (videoRef.current.srcObject !== mediaStream) {
      videoRef.current.srcObject = mediaStream;
    }
  }, [mediaStream?.id]);
  const showVideo =
    mediaStream &&
    mediaStream.getVideoTracks().length > 0 &&
    (!isScreenShareThumbnail ? !isVideoOff : true);

  // Apply border ONLY if it's the local user (isSelf) AND they are the active speaker.
  const showActiveSpeakerBorder = isSelf && isActiveSpeaker;

  return (
    <div
      className={`relative md:w-full w-36 h-36 landscape:w-full landscape:mt-1 md:h-auto aspect-video rounded-xl overflow bg-gray-800 shadow-md
      ${showActiveSpeakerBorder ? "border-4 border-[#13a8e1]" : ""}`}
    >
      {showVideo ? (
        <video
          ref={videoRef}
          autoPlay
          playsInline
          muted
          className={`absolute inset-0 w-full h-full ${isScreenShareThumbnail ? "object-contain" : "object-cover"}`}
        />
      ) : (
        <div className="flex items-center justify-center h-full">
          {isScreenShareThumbnail ? (
            <Monitor className="h-16 w-16 text-gray-400" />
          ) : (
            <Avatar className="h-16 w-16 text-4xl">
              <AvatarImage src={avatarSrc} alt={name} />
              <AvatarFallback className="bg-gray-700 text-white">
                {name.charAt(0)}
              </AvatarFallback>
            </Avatar>
          )}
        </div>
      )}

      <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black via-black/50 to-transparent p-2 flex items-center justify-between">
        <span className="text-white text-sm font-medium truncate">{name}</span>
        <div className="flex space-x-1">
          {/* Mic icon only for non-screen share thumbnails */}
          {!isScreenShareThumbnail &&
            (isMuted ? (
              <MicOff className="h-4 w-4 text-red-500" />
            ) : (
              <Mic className="h-4 w-4 text-white" />
            ))}
          {/* Video icon only for non-screen share thumbnails */}
          {!isScreenShareThumbnail &&
            (isVideoOff ? (
              <VideoOff className="h-4 w-4 text-red-500" />
            ) : (
              <Video className="h-4 w-4 text-gray-400" />
            ))}
          {/* Screen share icon for screen share thumbnails */}
          {isScreenShareThumbnail && <Monitor className="h-4 w-4 text-white" />}
        </div>
      </div>
      {isSelf && (
        <span className="absolute top-2 left-2 text-xs bg-[#0094ff] bg-opacity-50 text-white px-2 py-0.5 rounded-full">
          You
        </span>
      )}
    </div>
  );
};

export default ParticipantThumbnail;
